import { Injectable, Optional } from '@angular/core';
import { DropDownItem } from '@shared/models/drop-down-item';
import { IdName } from '@shared/models/id-name';
import { MaterialOption } from '@shared/models/material-option';
import { Specification } from '@shared/models/specification';
import { ShadeSystem } from '@shared/models/shade-system';
import { SpecIdWithMaterialIds } from '@shared/models/rx-rules-json-interface';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { getMeaningfulValue } from '@shared/utils/type-util';
import { Maybe } from '@/types/globals';
import { RxShadeSystemRulesService } from '@shared/services/rx-rules-helper/rx-shade-system-rules.service';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { RoleTypeEnum } from '@shared/models/role-type';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ProcedureMap } from '@shared/models/procedure-map';
import { OrderQuery } from '@modules/order/state/order-query';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { Material } from '@shared/models/material';
import { IntuitiveIndication } from '@modules/rx-intuitive-teeth-orchestrator/models';
import { RxIntuitiveTeethDiagramStore } from '@modules/rx-intuitive-teeth-orchestrator/state/rx-intuitive-teeth-diagram-store';
import { RxMaterialRulesService } from '@shared/services/rx-rules-helper/rx-material-rules.service';
import { toUnitTypeOrDefault, toUnitTypeInBridge } from '@modules/rx-intuitive-teeth-orchestrator/models/intuitive-indication';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';

export interface Resolution<T> {
	current: T | null;
	options: T[];
}

export interface ShadeSystemResolution extends Resolution<ShadeSystem> {
	isOtherShadeSystemSelected: boolean;
}

export interface CrownSelectionState {
	specification: Resolution<Specification>;
	material: Resolution<IdName<number>>;
	shadeSystem: ShadeSystemResolution;
	incisalBodyGingivalOptions: IdName<string>[];
	materialOption: Maybe<MaterialOption>;
}

export interface IntuitiveMainCrownFormData {
	material: Maybe<number | string>;
	specification: Maybe<number | string>;
	shadeSystem: Maybe<number>;
	body: Maybe<string>;
}

@Injectable()
export class RxIntuitiveCrownFacade {
	isNotDoctor$: Observable<boolean> = this.shellQuery.userRole$.pipe(
		map(userRole => userRole !== RoleTypeEnum.Doctor),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	shouldValidateForSend$: Observable<boolean> = this.shellQuery.shouldValidateForSend$;

	get procedureMap(): ProcedureMap | null {
		return this.orderQuery.procedureMap;
	}

	get teethFlat(): Tooth[] {
		return this.teethDiagramQuery.teethFlat;
	}

	constructor(
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly teethDiagramStore: TeethDiagramStore,
		/** Absent when crown is hosted outside the intuitive diagram injector (e.g. CDK overlay / implants modal). */
		@Optional() private readonly intuitiveDiagramStore: RxIntuitiveTeethDiagramStore | null,
		private readonly rxShadeSystemRulesService: RxShadeSystemRulesService,
		private readonly rxMaterialRulesService: RxMaterialRulesService,
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery
	) {}

	resolveSpecIdsWithMaterialIds(indicationId: IntuitiveIndication, isBridge = false): SpecIdWithMaterialIds[] {
		if (isBridge) {
			const bridgeUnitType = toUnitTypeInBridge(indicationId);

			if (bridgeUnitType == null) {
				return [];
			}

			return this.rxMaterialRulesService.getSpecIdsWithMaterialIdsForBridge(this.procedureMap, bridgeUnitType);
		}

		return this.rxMaterialRulesService.getSpecIdsWithMaterialIds(this.procedureMap, toUnitTypeOrDefault(indicationId));
	}

	resolveSpecification(specifications: Specification[], specificationId: Maybe<number | string>) {
		const emptyValues: (number | string | null | undefined)[] = [-1, null, undefined];

		if (!emptyValues.includes(specificationId)) {
			let current = specifications.find(({ Id }) => Id === specificationId);

			if (!current) {
				current = this.addHiddenOptionToTop(
					specificationId as number | string,
					this.rxMaterialRulesService.getSpecificationName(specificationId as number | string),
					specifications
				) as Specification;
			}

			return { current, options: specifications };
		}

		return { current: null, options: specifications };
	}

	resolveMaterial(materials: Material[], form: CrownForm) {
		if (form?.material != null) {
			const materialId = form?.material;
			let current = materials?.find(({ Id }) => Id === materialId) as DropDownItem<number>;

			if (!current) {
				current = this.addHiddenOptionToTop(
					materialId,
					this.rxMaterialRulesService.getMaterialName(materialId),
					materials as DropDownItem<number>[]
				);
			}

			return { current, options: materials };
		}

		return { current: null, options: materials };
	}

	resolveMaterialOption(materialId: Maybe<number | string>, specificationId: Maybe<number | string>, customLabMaterialGroup?: string) {
		if (!materialId) {
			return null;
		}

		return this.rxMaterialRulesService.getMaterialSpecOption(
			materialId,
			specificationId === null ? undefined : specificationId,
			customLabMaterialGroup
		);
	}

	resolveShadeSystem(shadeSystemId: Maybe<number>) {
		const { shadeSystem, options } = this.rxShadeSystemRulesService.getShadeSystemOptionsAndSelection(shadeSystemId ?? undefined);

		return {
			current: shadeSystem,
			options,
			isOtherShadeSystemSelected: shadeSystem?.Id === -1
		};
	}

	resolveIncisalBodyGingivalOptions(materialId: Maybe<number | string>, shadeSystemId: Maybe<number>) {
		if (!shadeSystemId) {
			return [];
		}

		const parsedMaterial = materialId ? parseInt(materialId as string, 10) : -1;

		return this.getIncisalBodyGingivalOptions({
			materialId: parsedMaterial,
			shadeSystemId
		});
	}

	resolveShadeBodySelection(form: CrownForm, isOtherShadeSystemSelected: boolean, incisalBodyGingivalOptions: IdName<string>[]) {
		if (isOtherShadeSystemSelected) {
			return form?.shadeBody ?? null;
		}

		return incisalBodyGingivalOptions?.find(option => option.Id === form?.shadeBody) ?? null;
	}

	buildSelectionState(form: CrownForm, indicationId: IntuitiveIndication, customLabMaterialGroup?: string, isBridge = false) {
		const specIdsWithMaterialIds = this.resolveSpecIdsWithMaterialIds(indicationId, isBridge);

		const availableSpecifications = this.rxMaterialRulesService.getFilteredSpecifications(specIdsWithMaterialIds.map(({ Id }) => Id));
		const specification = this.resolveSpecification(availableSpecifications, form?.specification);

		const specId = specification.current?.Id;
		const availableMaterials = this.getFilteredMaterialsBySpecifications(specId as number, specIdsWithMaterialIds);
		const material = this.resolveMaterial(availableMaterials, form);

		const materialOption = this.resolveMaterialOption(material.current?.Id, form?.specification, customLabMaterialGroup);

		const shadeSystem = this.resolveShadeSystem(form?.shadeSystem);
		const incisalBodyGingivalOptions = this.resolveIncisalBodyGingivalOptions(material.current?.Id, shadeSystem.current?.Id);

		return {
			specification,
			material,
			shadeSystem,
			incisalBodyGingivalOptions,
			materialOption
		};
	}

	/**
	 * Persists crown material / shade / specification from a {@link CrownForm} (e.g. `rx-intuitive-crown` `crownFormChanged`)
	 * into {@link TeethDiagramStore}, and marks teeth in {@link RxIntuitiveTeethDiagramStore} when that store was injected.
	 */
	updateTeethFromCrownForm(crownForm: CrownForm): void {
		const partialToothUpdate = {
			MaterialID: crownForm.material,
			ShadeSystemId: crownForm.shadeSystem,
			ShadeBody: crownForm.shadeBody,
			SpecificationId: crownForm.specification
		};

		this.teethDiagramStore.updateTeethByIds(crownForm.teethIds, partialToothUpdate);
		this.intuitiveDiagramStore?.markRecentlyEditedTeeth(crownForm.teethIds);
	}

	getMaterialsBySpecification(specId: Maybe<number | string>, indicationId: IntuitiveIndication, isBridge = false) {
		if (!specId || !indicationId) {
			return [];
		}

		const specIdsWithMaterialIds = this.resolveSpecIdsWithMaterialIds(indicationId, isBridge);

		return this.getFilteredMaterialsBySpecifications(specId as number, specIdsWithMaterialIds);
	}

	mapFormToCrownData(formRawValue: Record<string, DropDownItem | null>, teethIds: number[]): CrownForm {
		return {
			teethIds,
			material: formRawValue.material?.Id ?? null,
			shadeSystem: formRawValue.shadeSystem?.Id ?? null,
			shadeBody: getMeaningfulValue<string>(formRawValue?.body),
			specification: formRawValue.specification?.Id ?? null
		};
	}

	private getFilteredMaterialsBySpecifications(specId: number, specIdsWithMaterialIds: SpecIdWithMaterialIds[]) {
		const ids = this.rxMaterialRulesService.getMaterialIdsBySpecification(specId, specIdsWithMaterialIds);

		return this.rxMaterialRulesService.getFilteredMaterials(ids);
	}

	private getIncisalBodyGingivalOptions({ materialId, shadeSystemId }: { materialId: number; shadeSystemId: number }) {
		return this.rxShadeSystemRulesService.getIncisalBodyGingivalOptions({ materialId, shadeSystemId }) ?? [];
	}

	private addHiddenOptionToTop(id: number | string, name: string, optionList: DropDownItem[]): DropDownItem {
		const option = {
			Name: name,
			Id: id,
			Hidden: true
		};

		optionList?.unshift(option);

		return option;
	}
}
